import type { FormFieldFieldHelper } from '@ingka/form-field';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { shouldValidate } from '../../utils';
import { getLimit } from './util';

export type ControlledInputFieldProps<TFormData extends FieldValues = FieldValues> = {
  labelKey: string;
  control: Control<TFormData>;
  name: Path<TFormData>;
  disabled?: boolean;
  fieldHelper?: FormFieldFieldHelper;
  type?: 'number' | 'email' | 'hidden' | 'text' | 'password';
  suffix?: string;
  showCharacterLimit: boolean;
  upperLimit?: number;
  lowerLimit?: number;
};

export const ControlledInputField = <TFormData extends FieldValues = FieldValues>({
  control,
  labelKey,
  name,
  disabled = false,
  fieldHelper,
  type = 'text',
  suffix,
  showCharacterLimit,
  upperLimit,
  lowerLimit,
}: ControlledInputFieldProps<TFormData>) => {
  const { t } = useTranslation();

  const transformErrorMessage = (errorMessage: string | undefined) => {
    return errorMessage ? t(errorMessage, getLimit({ upperLimit, lowerLimit, errorMessage })) : '';
  };

  return (
    <Controller
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <FormField
          shouldValidate={shouldValidate(error)}
          valid={!error}
          characterLimit={showCharacterLimit ? upperLimit : undefined}
          validation={{
            msg: transformErrorMessage(error?.message),
            type: 'error',
          }}
          fieldHelper={fieldHelper}
        >
          <InputField
            id={`lottery-form-${name}`}
            label={`${t(labelKey)}`}
            type={type}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            suffixLabel={suffix}
            value={value ? DOMPurify.sanitize(value) : ''}
            data-testid={`input-${name.replace(/\./g, '-')}`}
          />
        </FormField>
      )}
      name={name}
      control={control}
    />
  );
};
